import { Box, ClickAwayListener, MenuItem, MenuList, Paper, Popper, TextField } from '@mui/material'
import React, { forwardRef, useEffect, useRef, useState } from 'react'

type CustomTimePickerProps = {
  label?: string
  onChange: (value: string) => void
  interval?: number // in minutes
  defaultValue?: string
  isFirstTimePicker?: boolean
  onTimeSelected?: () => void
}

const timeRegex = /^(Closed|Open 24 hours|(?:1[0-2]|0?[1-9]):[0-5][0-9] (AM|PM|am|pm))$/

const CustomTimePicker = forwardRef<HTMLInputElement, CustomTimePickerProps>((props, ref) => {
  const {
    label = 'Select time',
    onChange,
    interval = 30,
    defaultValue,
    isFirstTimePicker = false,
    onTimeSelected,
  } = props

  const [value, setValue] = useState<string>(defaultValue || '')
  const [isValid, setIsValid] = useState<boolean>(true)
  const [open, setOpen] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const selectedItemRef = useRef<HTMLLIElement | null>(null)

  // Forward the ref
  useEffect(() => {
    if (typeof ref === 'function') {
      ref(inputRef.current)
    } else if (ref) {
      ref.current = inputRef.current
    }
  }, [ref])

  // Generate times inside the component
  const generateTimeOptions = (interval: number): string[] => {
    const times: string[] = []
    const start = new Date()
    start.setHours(0, 0, 0, 0)

    for (let i = 0; i < (24 * 60) / interval; i++) {
      const hours = start.getHours()
      const minutes = start.getMinutes()

      const suffix = hours >= 12 ? 'PM' : 'AM'
      const displayHours = hours % 12 === 0 ? 12 : hours % 12
      const displayMinutes = minutes.toString().padStart(2, '0')

      times.push(`${displayHours}:${displayMinutes} ${suffix}`)
      start.setMinutes(start.getMinutes() + interval)
    }

    return times
  }

  const predefinedTimes = generateTimeOptions(interval)

  const normalizeTime = (input: string): string => {
    return input
      .trim()
      .replace(/\./g, ':')
      .replace(/(am|pm)$/i, ' $1')
      .replace(/\s+/g, ' ')
      .toUpperCase()
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    setValue(raw)
    setIsValid(true)
  }

  const handleMenuItemClick = (option: string) => {
    setValue(option)
    setIsValid(true)
    setOpen(false)
    onChange(option)

    // If this is the first time picker, trigger the callback to focus the next one
    if (isFirstTimePicker && onTimeSelected) {
      // Use setTimeout to ensure the focus happens after the current event cycle
      setTimeout(() => {
        onTimeSelected()
      }, 0)
    }
  }

  const handleClickAway = () => {
    setOpen(false)
  }

  const handleBlur = () => {
    // Normalize the time input when the field loses focus
    const normalized = normalizeTime(value)
    setValue(normalized)
    setIsValid(timeRegex.test(normalized))
    onChange(normalized)
    
    // Use setTimeout to allow click events on menu items to fire first
    setTimeout(() => {
      setOpen(false)
    }, 100)
  }

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }, 0)
    }
  }, [open])

  return (
    <Box sx={{ padding: 2, position: 'relative', minwidth: 138 }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>
          <TextField
            inputRef={inputRef}
            fullWidth
            label={label}
            value={value}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            onBlur={handleBlur}
            error={!isValid}
            sx={{
              '& .MuiInputBase-input:focus': {
                outline: 'none',
                boxShadow: 'none',
              },
            }}
          />

          <Popper
            open={open}
            anchorEl={inputRef.current}
            placement='bottom-start'
            style={{
              zIndex: 1300,
              width: inputRef.current ? inputRef.current.clientWidth : undefined,
            }}
          >
            <Paper elevation={3}>
              <MenuList sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {predefinedTimes.map((option, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => handleMenuItemClick(option)}
                    selected={option === value}
                    ref={option === value ? selectedItemRef : null}
                  >
                    {option}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>
    </Box>
  )
})

CustomTimePicker.displayName = 'CustomTimePicker'

export default CustomTimePicker
